import React, { useContext } from 'react';

import chatTokens from '@amzn/meridian-tokens/base/icon/chat';
import checkLargeTokens from '@amzn/meridian-tokens/base/icon/check-large';
import minusTokens from '@amzn/meridian-tokens/base/icon/minus';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ICellRendererParams } from 'ag-grid-community';
import { isNil, isUndefined } from 'lodash';
import { getUserElement } from 'src/components/RateCardHeader';
import { TerritoryFlag } from 'src/components/TerritoryFlag';
import { CommentCategory, RateCardStatus, Status } from 'src/constants';
import { AppContext } from 'src/contexts/appContext';
import BlueDot from 'src/images/bluedot.svg';
import GreenDot from 'src/images/greendot.svg';
import GreyDot from 'src/images/greydot.svg';
import { ICommentSheetAttributesProps } from 'src/types/ICommentSheetAttributes';
import { isOnGammaProd } from 'src/util/config';

interface IServiceStatusCellRendererParams extends ICellRendererParams {
    displayText?: boolean;
}

interface ICommentCellRendererParams extends ICellRendererParams {
    category: string;
    deselectAllAgGridRows: () => void;
    commentSheetAttributes: ICommentSheetAttributesProps;
}

export const getStatusCellRenderer = (params: IServiceStatusCellRendererParams) => {
    const displayText = params.displayText ? params.displayText : false;

    switch (params.value) {
        case Status.Active:
        case Status.Active.toUpperCase():
            return (
                <Row>
                    <span>
                        <img src={GreenDot} alt="active service" />
                    </span>
                    <span>{displayText ? params.value : ' '}</span>
                </Row>
            );
        case Status.Upcoming:
        case Status.Upcoming.toUpperCase():
        case RateCardStatus.Draft:
            return (
                <Row>
                    <span>
                        <img src={BlueDot} alt="upcoming service" />
                    </span>
                    <span>{displayText ? params.value : ' '}</span>
                </Row>
            );
        case Status.Ended:
        case Status.Ended.toUpperCase():
        case Status.Canceled:
        case Status.Canceled.toUpperCase():
        case RateCardStatus.Inactive:
        case RateCardStatus.Archived:
            return (
                <Row>
                    <span>
                        <img src={GreyDot} alt="ended service" />
                    </span>
                    <span>{displayText ? params.value : ' '}</span>
                </Row>
            );
        case `${Status.Active}, ${Status.Upcoming}`:
        case `${Status.Active.toUpperCase()}, ${Status.Upcoming.toUpperCase()}`:
            return (
                <>
                    <img src={GreenDot} alt="active service" />
                    &nbsp;
                    <img src={BlueDot} alt="upcoming service" />
                </>
            );
        default:
            return <span>—</span>;
    }
};

export const getPhoneToolLinkCellRenderer = (params: ICellRendererParams) => {
    return params.value ? getUserElement(params.value, true) : '—';
};

export const getCommentsRenderer = (params: ICommentCellRendererParams) => {
    const { setCommentSheetAttributes, commentStatus } = useContext(AppContext);
    const onClickCommentSheetHandler = (onClickparams: any) => {
        let categoryUniqueColumnId: number;
        if (params.category === CommentCategory.Deals) {
            categoryUniqueColumnId = onClickparams.data.entityContractId;
        } else if (params.category === CommentCategory.Ratecards) {
            categoryUniqueColumnId = onClickparams.data.contractId;
        } else if (params.category === CommentCategory.Vendorcodes) {
            categoryUniqueColumnId = onClickparams.data.entityVendorCodeId;
        }

        params.deselectAllAgGridRows();
        setCommentSheetAttributes((prevState) => ({
            ...prevState,
            categoryUniqueColumnId: categoryUniqueColumnId,
            openCommentSheet:
                prevState.categoryUniqueColumnId === params.data.entityContractId ||
                prevState.categoryUniqueColumnId === null
                    ? !prevState.openCommentSheet
                    : true,
            highlightedButton: onClickparams.node.id
        }));
    };
    return (
        <Button
            ref={(ref) => {
                if (!ref) return;

                ref.onclick = (e: any) => {
                    e.stopPropagation();
                    onClickCommentSheetHandler(params);
                };
            }}
            type={
                params.node.id == params.commentSheetAttributes.highlightedButton?.toString() &&
                params.commentSheetAttributes.openCommentSheet
                    ? 'primary'
                    : 'link'
            }
            size="small"
            disabled={isOnGammaProd()}
        >
            <Icon
                className={
                    commentStatus.includes(params.data.entityContractId)
                        ? params.node.id == params.commentSheetAttributes.highlightedButton?.toString() &&
                          params.commentSheetAttributes.openCommentSheet
                            ? 'comment-icon-dark-mode'
                            : 'comment-icon'
                        : ''
                }
                tokens={chatTokens}
            />
        </Button>
    );
};

export const getPaperPlanBooleanAttributeRenderer = (params: ICellRendererParams) => {
    switch (params.value) {
        case true:
            return (
                <Row>
                    <span>
                        <Icon className="check-icon" tokens={checkLargeTokens} />
                    </span>
                    <span>Yes</span>
                </Row>
            );
        case false:
            return (
                <Row>
                    <span>
                        <Icon className="minus-icon" tokens={minusTokens} />
                    </span>
                    <span>No</span>
                </Row>
            );
        default:
            return <span>—</span>;
    }
};

export const getTerritoryFlagRenderer = (
    params: ICellRendererParams,
    showTerritoryName = false,
    showTerritoryCode?: boolean
) => {
    return (
        <Row height="100%" spacing="none">
            {isNil(params.value) ? (
                '—'
            ) : (
                <TerritoryFlag
                    territoryCode={params.value}
                    showTerritoryCode={isUndefined(showTerritoryCode) ? !showTerritoryName : showTerritoryCode}
                    showTerritoryName={showTerritoryName}
                    isGridDisplay={true}
                />
            )}
        </Row>
    );
};
